import { initSentryBrowser, initSentryVue } from './modules/sentry';
initSentryBrowser();

import.meta.glob(['../img/**']);

import './modules/tracking';
import './modules/unregister-service-workers';
import './modules/page-tracking-events';
import './modules/tracking-events';
import './modules/menu';
import './modules/carousel/carousel';
import './modules/videobox';
import './modules/modal';
import './modules/sticky-apply-now';
import './modules/seo_accordion';
import './modules/seo_collapse';
import './modules/faq';
import './modules/poeditorHelpers';

// For the pie cookie banner styling
import '@justeattakeaway/pie-css';

import { hotjar } from './modules/tracking';
import { saveQueryParams } from './modules/query_utils';
import { getAATestFeatureValue } from './modules/ab-tests/feature-management';
import { createPinia } from 'pinia';
import { createApp, defineAsyncComponent } from 'vue';
import FloatingVue from 'floating-vue';

import { initJobPostings } from './modules/job-postings';
import { initJobPostingsLocationSelectorApp } from './modules/job-postings-location-selector-app';
import { $, $$, isInViewport } from './modules/utils';
import { mountWithFallback } from './modules/mountWithFallback';
import { initCookieStatement } from './modules/cookie-statement';
import { initDeeplinkFallback } from './modules/deeplink-fallback';

// Staging-only PoEditor key inspector (?show_poeditor_terms=1), only loaded when the server enabled it
if (window.poeditorDebug) {
    import('./modules/poeditor-debug').then(({ initPoEditorDebug }) => initPoEditorDebug());
}

hotjar.inject().then();
initDeeplinkFallback();

(async () => {
    document.addEventListener('DOMContentLoaded', () => {
        // Logic for adding lazy-loading
        const allTheImages = $$('img');
        allTheImages.forEach((image) => {
            if (isInViewport(image)) {
                image.setAttribute('loading', 'eager');
            } else {
                image.setAttribute('loading', 'lazy');
            }
        });

        // Animated images
        const animatedImages = $$(`
            .what-you-need__image img,
            .carousel__slide:first-of-type .testimonial img,
            .carousel__slide:first-of-type .blog-index__category-post-image img,
            .blog-section img,
            .category__image img
        `);

        const observer = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('img-reveal'); // Apply animation class
                    observer.unobserve(entry.target); // Stop observing once animated
                }
            });
        });
        animatedImages.forEach((image) => {
            if (!isInViewport(image)) {
                observer.observe(image); // Only observe images outside the viewport
            }
        });

        // fixed blog posts table cols width for staging
        $$('.blog-content table colgroup col').forEach((col) => {
            col.style.width = '100%';
        });

        // Hero Disclaimer
        const heroDisclaimer = $('.hero-disclaimer');
        const closeDisclaimer = $('.disclaimer__close');

        if (heroDisclaimer && !localStorage.getItem('heroDisclaimerHidden')) {
            heroDisclaimer.style.display = 'block';
        }

        if (closeDisclaimer) {
            closeDisclaimer.addEventListener('click', () => {
                if (heroDisclaimer) {
                    heroDisclaimer.style.display = 'none';
                    localStorage.setItem('heroDisclaimerHidden', 'true');
                }
            });
        }
    });

    initCookieStatement();
    initJobPostings();

    window.newCityParamPresent = saveQueryParams();

    // initialize pinia once if needed
    const requirePinia = [
        '#signup-page-container',
        '[data-salary-calculator-container]',
        '#pie_cookie_bar',
        '[data-hero-city-selector-app]',
    ];
    const piniaNeeded = requirePinia.some((selector) => document.querySelector(selector));
    let pinia = null;
    if (piniaNeeded) {
        pinia = createPinia();
    }

    if ($('#breadcrumbs')) {
        const Breadcrumbs = defineAsyncComponent(() => import('./components/Breadcrumbs.vue'));
        const BreadcrumbsVue = createApp(Breadcrumbs).mount('#breadcrumbs');
        initSentryVue(BreadcrumbsVue);
    }

    if ($('#workingAreasMap')) {
        const WorkingAreasMap = defineAsyncComponent(() => import('./components/WorkingAreasMap.vue'));
        const WorkingAreasMapVue = createApp(WorkingAreasMap).mount('#workingAreasMap');
        initSentryVue(WorkingAreasMapVue);
    }

    // Vue components aren't returned in initial HTML server response, which can hurt SEO.
    // So we first first show the fallback, and then hide it when the Vue component is ready. (#1405)
    const salaryCalculatorContainers = $$('[data-salary-calculator-container]');
    if (salaryCalculatorContainers.length > 0) {
        const SalaryCalculator = defineAsyncComponent(() =>
            import('./components/salary-calculator/SalaryCalculator.vue'),
        );
        salaryCalculatorContainers.forEach((salaryCalculatorContainer) => {
            const mountTarget = salaryCalculatorContainer.querySelector('[data-salary-calculator-app]');
            if (!mountTarget) return;

            const SalaryCalculatorVue = createApp(SalaryCalculator).use(pinia);
            mountWithFallback(
                SalaryCalculatorVue,
                mountTarget,
                salaryCalculatorContainer.querySelectorAll('[data-salary-calculator-fallback]'),
            );
            initSentryVue(SalaryCalculatorVue);
        });
    }

    initJobPostingsLocationSelectorApp();

    // Mount the city selector of the Dual model per city neutral page hero (Basic with City Selector)
    const heroCitySelectorEl = $('[data-hero-city-selector-app]');
    if (heroCitySelectorEl) {
        const HeroCitySelector = defineAsyncComponent(() => import('./components/HeroCitySelector.vue'));
        const heroCitySelectorVue = createApp(HeroCitySelector).use(pinia);
        mountWithFallback(heroCitySelectorVue, heroCitySelectorEl, $$('[data-hero-city-selector-fallback]'));
        initSentryVue(heroCitySelectorVue);
    }

    // Mount partners list when the partners content block is on the page
    if ($('[data-partners-list]') && window.partnersSection) {
        const PartnersList = defineAsyncComponent(() => import('./components/partners/PartnersList.vue'));
        const { partners = [], cities = [] } = window.partnersSection;
        const partnersListVue = createApp(PartnersList, { partners, cities });
        partnersListVue.mount('[data-partners-list]');
        initSentryVue(partnersListVue);
    }

    // HeaderLanguageSelector is used on every page
    const HeaderLanguageSelector = defineAsyncComponent(() =>
        import('./components/hamburger_menu/HeaderLanguageSelector.vue'),
    );
    const languageSelectorVue = createApp(HeaderLanguageSelector);
    languageSelectorVue.mount('#language-selector-mobile');
    initSentryVue(languageSelectorVue);

    // HeaderHamburgerMenu is used on every page
    const HeaderHamburgerMenu = defineAsyncComponent(() =>
        import('./components/hamburger_menu/HeaderHamburgerMenu.vue'),
    );
    const hamburgerMenuVue = createApp(HeaderHamburgerMenu);
    hamburgerMenuVue.mount('#hamburger-menu-mobile');
    initSentryVue(hamburgerMenuVue);

    // CountryLanguageSelector is used on every page
    const CountryLanguageSelector = defineAsyncComponent(() =>
        import('./components/country-language-selector/CountryLanguageSelectorHeader.vue'),
    );
    const CountryLanguageSelectorVue = createApp(CountryLanguageSelector);
    CountryLanguageSelectorVue.mount('#country-language-selector');
    initSentryVue(hamburgerMenuVue);

    if ($('#signup-page-container')) {
        const SignupForm = defineAsyncComponent(() => import('./components/signup-multi-form/SignupDynamicForm.vue'));
        const SignupFormVue = createApp(SignupForm);
        SignupFormVue.use(pinia).use(FloatingVue).mount('#signup-page-container');
        initSentryVue(SignupFormVue);
    }

    // Loads Pie cookie component if needed (just for France & Denmark for now)
    if ($('#pie_cookie_bar')) {
        const CookieBar = defineAsyncComponent(() => import('./components/CookieBar.vue'));
        const CookieBarVue = createApp(CookieBar);
        CookieBarVue.use(pinia).mount('#pie_cookie_bar');
        initSentryVue(CookieBarVue);
    }
})();
// Simply query the JET FM A/A test and do nothing with the value.
// The experiment event will be tracked by the JET FM JavaScript SDK, using the GTM hook.
// See feature-management.js for more details.
getAATestFeatureValue().then();
